import type { UseMutateAsyncFunction } from '@tanstack/react-query'
import type { ReactNode } from 'react'

import type { ConfigurationFileContextType } from '@/features/configuration/contexts/ConfigurationFileContext'
import {
  ConfigurationFileContext,
  useConfigurationFileContext,
} from '@/features/configuration/contexts/ConfigurationFileContext'
import type { getControllerConfigLatestVersionParams } from '@/features/controller/configuration/endpoints/controllerConfiguration'
import type { UseControllerConfigLatestVersionQueryResult } from '@/features/controller/configuration/hooks/useLatestControllerConfiguration'
import {
  useInvalidateLatestControllerConfiguration,
  useLatestControllerConfiguration,
} from '@/features/controller/configuration/hooks/useLatestControllerConfiguration'
import { useUpdateControllerConfigLatestVersionMutation } from '@/features/controller/configuration/hooks/useUpdateControllerConfigLatestVersionMutation'
import type { ControllerConfigVersion, ControllerConfigVersionUpdate } from '@/features/controller/configuration/types'
import type { ControllerConfigTypeId } from '@/features/controller/configuration/types'

/** @deprecated Use {@link ConfigurationFileContextType} from the shared configuration module instead. */
export type ControllerConfigurationFileContextType = ConfigurationFileContextType

/** Re-exported for backwards compatibility; this is the shared, feature-agnostic configuration context. */
export const ControllerConfigurationFileContext = ConfigurationFileContext

export const useControllerConfigurationFileContext = useConfigurationFileContext

interface ControllerConfigurationFileProviderProps {
  children: ReactNode
  configTypeId: ControllerConfigTypeId
  controllerId: string
}

export function makeConfigFileContextValue(
  controllerId: string,
  configTypeId: ControllerConfigTypeId,
  queryResult: UseControllerConfigLatestVersionQueryResult,
  mutation: ControllerConfigurationFileMutation,
  invalidate: (params: getControllerConfigLatestVersionParams) => Promise<void>,
): ControllerConfigurationFileContextType {
  return {
    isLatestVersionFetched: queryResult.isFetched,
    isLatestVersionLoading: queryResult.isLoading,
    isUpdatePending: mutation.isPending,
    latestVersion: queryResult.latestConfigVersion,
    updateAndInvalidate: async (update: ControllerConfigVersionUpdate) => {
      await mutation.updateControllerConfigLatestVersion(update)
      await invalidate({ controllerId, configTypeId })
    },
  }
}

type ControllerConfigurationFileMutation = {
  isPending: boolean
  updateControllerConfigLatestVersion: UseMutateAsyncFunction<
    ControllerConfigVersion,
    Error,
    ControllerConfigVersionUpdate,
    unknown
  >
}

export function ControllerConfigurationFileProvider({
  children,
  configTypeId,
  controllerId,
}: Readonly<ControllerConfigurationFileProviderProps>) {
  const latest = useLatestControllerConfiguration({ controllerId, configTypeId })
  const mutation = useUpdateControllerConfigLatestVersionMutation(controllerId, configTypeId)
  const invalidateLatestConfig = useInvalidateLatestControllerConfiguration()

  const value = makeConfigFileContextValue(controllerId, configTypeId, latest, mutation, invalidateLatestConfig)

  return (
    <ControllerConfigurationFileContext.Provider value={value}>{children}</ControllerConfigurationFileContext.Provider>
  )
}
